import React, { useState, useRef, useEffect } from 'react';

/**
 * Month labels for display
 */
const MONTH_LABELS = {
  1: 'Jan', 2: 'Feb', 3: 'Mär', 4: 'Apr', 5: 'Mai', 6: 'Jun',
  7: 'Jul', 8: 'Aug', 9: 'Sep', 10: 'Okt', 11: 'Nov', 12: 'Dez'
};

/**
 * FloweringMonthsSlider - Range slider for selecting flowering months
 *
 * This component is extracted to prevent re-renders of the parent
 * PlantCatalogSidebar when the slider values change.
 *
 * Uses debouncing (similar to PhSlider) to ensure smooth slider movement.
 *
 * @param {Object} props - Component props
 * @param {Object} props.value - Current month range {start: 1, end: 12} or null
 * @param {Function} props.onChange - Callback when range changes (debounced)
 */
const FloweringMonthsSlider = ({ value, onChange }) => {
  // Local state for smooth slider movement
  const [localStart, setLocalStart] = useState(value?.start || 1);
  const [localEnd, setLocalEnd] = useState(value?.end || 12);
  const timeoutRef = useRef(null);

  // Store current values in refs to avoid stale closure issues
  const currentStartRef = useRef(localStart);
  const currentEndRef = useRef(localEnd);

  // Keep refs in sync with state
  useEffect(() => {
    currentStartRef.current = localStart;
  }, [localStart]);

  useEffect(() => {
    currentEndRef.current = localEnd;
  }, [localEnd]);

  // Sync with parent value when it changes externally (e.g., reset) — adjusted
  // during render rather than in an effect to avoid a setState-in-effect cascade.
  // Track the primitive start/end (not the object identity) so an unstable
  // `value` reference from the parent can't trigger a render loop.
  const nextStart = value?.start || 1;
  const nextEnd = value?.end || 12;
  const [prevStart, setPrevStart] = useState(nextStart);
  const [prevEnd, setPrevEnd] = useState(nextEnd);
  if (nextStart !== prevStart || nextEnd !== prevEnd) {
    setPrevStart(nextStart);
    setPrevEnd(nextEnd);
    setLocalStart(nextStart);
    setLocalEnd(nextEnd);
  }

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  /**
   * Handle start month change - updates local state immediately, debounces parent update
   */
  const handleStartChange = (e) => {
    const newStart = parseInt(e.target.value);

    // Update local state immediately for smooth UI
    setLocalStart(newStart);

    // Clear any pending timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Debounce the parent update (300ms)
    // Use ref to get current end value to avoid stale closure
    timeoutRef.current = setTimeout(() => {
      if (onChange) {
        onChange({ start: newStart, end: currentEndRef.current });
      }
    }, 300);
  };

  /**
   * Handle end month change - updates local state immediately, debounces parent update
   */
  const handleEndChange = (e) => {
    const newEnd = parseInt(e.target.value);

    // Update local state immediately for smooth UI
    setLocalEnd(newEnd);

    // Clear any pending timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Debounce the parent update (300ms)
    // Use ref to get current start value to avoid stale closure
    timeoutRef.current = setTimeout(() => {
      if (onChange) {
        onChange({ start: currentStartRef.current, end: newEnd });
      }
    }, 300);
  };

  /**
   * Reset to no filter
   */
  const handleReset = () => {
    setLocalStart(1);
    setLocalEnd(12);
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    if (onChange) {
      onChange(null);
    }
  };

  // Check if filter is active (not default 1-12 or null)
  const isActive = value && (value.start !== 1 || value.end !== 12);

  return (
    <div className="w-full px-2 py-2">
      {/* Label */}
      <span className="block text-xs font-medium text-gray-600 mb-2">
        Blütezeit
      </span>

      {/* Dual Range Sliders */}
      <div className="space-y-3">
        {/* Start Month Slider */}
        <div className="flex items-center gap-3">
          <span className="text-xs text-gray-500 w-10">Von:</span>
          <div className="flex-1">
            <input
              type="range"
              min="1"
              max="12"
              step="1"
              value={localStart}
              onChange={handleStartChange}
              className="w-full h-2 bg-gray-200 rounded-full appearance-none cursor-pointer slider-emerald"
              style={{
                background: `linear-gradient(to right, #10b981 0%, #10b981 ${
                  ((localStart - 1) / 11) * 100
                }%, #e5e7eb ${((localStart - 1) / 11) * 100}%, #e5e7eb 100%)`,
              }}
              aria-label="Blütezeit Start-Monat"
            />
            <div className="flex justify-between text-[10px] text-gray-500 mt-1 px-0.5">
              <span>Jan</span>
              <span>Dez</span>
            </div>
          </div>
          <span className="text-sm font-semibold text-emerald-600 w-10">
            {MONTH_LABELS[localStart]}
          </span>
        </div>

        {/* End Month Slider */}
        <div className="flex items-center gap-3">
          <span className="text-xs text-gray-500 w-10">Bis:</span>
          <div className="flex-1">
            <input
              type="range"
              min="1"
              max="12"
              step="1"
              value={localEnd}
              onChange={handleEndChange}
              className="w-full h-2 bg-gray-200 rounded-full appearance-none cursor-pointer slider-emerald"
              style={{
                background: `linear-gradient(to right, #10b981 0%, #10b981 ${
                  ((localEnd - 1) / 11) * 100
                }%, #e5e7eb ${((localEnd - 1) / 11) * 100}%, #e5e7eb 100%)`,
              }}
              aria-label="Blütezeit End-Monat"
            />
            <div className="flex justify-between text-[10px] text-gray-500 mt-1 px-0.5">
              <span>Jan</span>
              <span>Dez</span>
            </div>
          </div>
          <span className="text-sm font-semibold text-emerald-600 w-10">
            {MONTH_LABELS[localEnd]}
          </span>
        </div>
      </div>

      {/* Reset Button */}
      {isActive && (
        <button
          onClick={handleReset}
          className="mt-2 text-xs text-emerald-600 hover:text-emerald-700 underline"
        >
          Blütezeit-Filter zurücksetzen
        </button>
      )}
    </div>
  );
};

export default React.memo(FloweringMonthsSlider);
